import {bucket} from '../../../../../aws-config'
import type {dbItem} from '@/shared/utils/crud-helper/models'
import {isShareUnavailable, listShareFiles, requestShareDownloadUrl} from '../../../sharing/api'
import {
  createShareFolder,
  deleteShareItems,
  renameShareItem,
  ShareTransferMode,
  startShareTransfer,
} from '../../../sharing/shareFilesApi'
import {waitForShareOperation} from '../../../sharing/shareOperations'
import {uploadFileToShare} from '../../../sharing/shareUpload'
import {FileStore, OperationOptions, RemoveResult, ShareRoot, UploadOptions} from './types'

/** What locates a shared folder's files: enough to build a key, never to authorize one. */
export type ShareLocation = Pick<ShareRoot, 'id' | 'ownerIdentityId' | 'folderPath'>

/**
 * Thrown, without a request, by the write operations of a folder shared with Can view (D6). The
 * browser does not offer them there; the server would answer 403 `insufficient_access` anyway.
 */
export class ShareReadOnlyError extends Error {
  constructor(operation: string) {
    super(`${operation} needs Can edit access to this shared folder.`)
    this.name = 'ShareReadOnlyError'
  }
}

/** `delete` takes at most this many paths per call. */
const DELETE_PATHS_PER_CALL = 100

/** The listing endpoint's `path`: `''` for the shared folder itself, else a folder ending in `/`. */
const listingPath = (folder: string) => (folder === '' ? '' : `${folder}/`)

/*
  Presigned GET URLs, by share. The server signs each with a fresh 15-minute session (E11); a URL is
  reused for at most 10 minutes, so nothing cached here outlives the credentials behind it, and a
  viewer handed one still has minutes left to read the file. Dropped for a share as soon as the
  server says the share is gone (forgetSharePresignedUrls).
*/
const URL_REUSE_MARGIN_MS = 5 * 60 * 1000
const presignedUrls = new Map<string, Map<string, {url: string; reusableUntil: number}>>()

/** Forget every presigned URL minted for this share (on a 403/404, or when access changes). */
export function forgetSharePresignedUrls(shareId: string): void {
  presignedUrls.delete(shareId)
}

/** Test seam: forget every share's URLs. */
export function forgetAllSharePresignedUrls(): void {
  presignedUrls.clear()
}

/**
 * A presigned URL for a file in a shared folder, `path` relative to it. `download` asks for a URL
 * that makes the browser save the file rather than show it.
 */
export async function getShareFileUrl(
  shareId: string,
  path: string,
  download = false
): Promise<string> {
  const cacheKey = `${download ? 'save' : 'view'}:${path}`
  const cached = presignedUrls.get(shareId)?.get(cacheKey)
  if (cached && cached.reusableUntil > Date.now()) {
    return cached.url
  }
  try {
    const {url, expiresIn} = await requestShareDownloadUrl(shareId, path, download)
    const reusableFor = expiresIn * 1000 - URL_REUSE_MARGIN_MS
    if (url && reusableFor > 0) {
      const forShare = presignedUrls.get(shareId) ?? new Map()
      forShare.set(cacheKey, {url, reusableUntil: Date.now() + reusableFor})
      presignedUrls.set(shareId, forShare)
    }
    return url
  } catch (error) {
    if (isShareUnavailable(error)) {
      forgetSharePresignedUrls(shareId)
    }
    throw error
  }
}

/** The storage key of a file in a shared folder: `<ownerIdentityId>/<folderPath><path>` (E1). */
export function shareObjectKey(share: ShareLocation, path: string): string {
  return `${share.ownerIdentityId}/${share.folderPath}${path}`
}

/**
 * The `s3://` URI of a file in a shared folder, in the form `getFileS3Url` gives the user's own
 * files. It names the owner's key, so it is the same string for every member (E1).
 */
export function shareFileS3Url(share: ShareLocation, path: string): string {
  return encodeURI(`s3://${bucket}/${shareObjectKey(share, path)}`)
}

/**
 * The path inside `share` of an `s3://<bucket>/<identity>/<key>` URI, or undefined when the URI is
 * not a file inside that shared folder. Strict on purpose: a viewer must never be handed a URL for a
 * key other than the one it opened.
 */
export function sharePathOfObjectUri(objectUri: string, share: ShareLocation): string | undefined {
  const segments = objectUri.replace(/^s3:\/\//, '').split('/')
  if (segments.length < 3) {
    return undefined
  }
  let identity: string
  let key: string
  try {
    identity = decodeURIComponent(segments[1])
    key = decodeURIComponent(segments.slice(2).join('/'))
  } catch {
    return undefined
  }
  if (identity !== share.ownerIdentityId || !key.startsWith(share.folderPath)) {
    return undefined
  }
  const path = key.slice(share.folderPath.length)
  return path === '' || path.endsWith('/') ? undefined : path
}

/** A destination folder as the transfer endpoint takes it: `''`, or `a/b/`. */
const transferFolder = (folder: string) => (folder === '' ? '' : `${folder.replace(/\/+$/, '')}/`)

/**
 * A shared folder (`root`), through the sharing API. Paths are relative to the shared folder, as
 * the listing returns them.
 *
 * With Can edit (P1-F4) it writes too: uploads (presigned POST or multipart), folders, delete, and
 * rename, move and copy inside the shared folder, which run in the background and are polled until
 * they end. With Can view every write throws {@link ShareReadOnlyError} without a request; the
 * browser does not offer them.
 */
export function createShareStore(root: ShareRoot): FileStore {
  const writable = root.access !== 'view'
  const write =
    <A extends unknown[], R>(operation: string, run: (...args: A) => Promise<R>) =>
    (...args: A): Promise<R> =>
      writable ? run(...args) : Promise.reject(new ShareReadOnlyError(operation))

  const progressOf = (options?: OperationOptions) =>
    options?.onProgress
      ? {
          onProgress: (op: {done?: number; objects?: number}) =>
            options.onProgress?.(op.done ?? 0, op.objects ?? 0),
        }
      : {}

  const transfer = async (
    mode: ShareTransferMode,
    sources: string[],
    destinationFolder: string,
    options?: OperationOptions
  ) => {
    const {operationId} = await startShareTransfer(root.id, {
      mode,
      from: 'share',
      to: 'share',
      sources,
      destination: transferFolder(destinationFolder),
    })
    return waitForShareOperation(root.id, operationId, progressOf(options))
  }

  const removeMany = async (paths: string[]): Promise<RemoveResult> => {
    const result: RemoveResult = {deleted: 0, failed: []}
    for (let i = 0; i < paths.length; i += DELETE_PATHS_PER_CALL) {
      const {deleted, failed} = await deleteShareItems(
        root.id,
        paths.slice(i, i + DELETE_PATHS_PER_CALL)
      )
      result.deleted += deleted ?? 0
      result.failed.push(...(failed ?? []))
    }
    return result
  }

  return {
    root,
    list: async (folder) => {
      try {
        const items = await listShareFiles(root.id, listingPath(folder))
        // Public links are the owner's alone (E9). The index row carries the owner's link id; a
        // member's browser must not offer "Copy link" for it.
        return items.map(({publicShareId, ...item}: dbItem) => item)
      } catch (error) {
        if (isShareUnavailable(error)) {
          forgetSharePresignedUrls(root.id)
        }
        throw error
      }
    },
    download: async (path, asBlob) => {
      // A single file opens in a new tab as a download; several are fetched and zipped.
      const url = await getShareFileUrl(root.id, path, !asBlob)
      if (!asBlob) {
        return url
      }
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`)
      }
      return response.blob()
    },
    upload: write('Upload', (path: string, file: File, options?: UploadOptions) =>
      uploadFileToShare(root.id, path, file, options)
    ),
    createFolder: write('Creating folders', (path: string) =>
      createShareFolder(root.id, `${path.replace(/\/+$/, '')}/`)
    ),
    remove: write('Delete', async (path: string) => {
      const {failed} = await removeMany([path])
      if (failed.length > 0) {
        throw new Error(`"${path}" could not be deleted.`)
      }
      return path
    }),
    removeMany: write('Delete', removeMany),
    // Renames in place: the new name is the destination's last segment.
    rename: write(
      'Rename',
      async (source: string, destination: string, options?: OperationOptions) => {
        const newName = destination.replace(/\/+$/, '').split('/').pop() ?? ''
        const {operationId} = await renameShareItem(root.id, source, newName)
        return waitForShareOperation(root.id, operationId, progressOf(options))
      }
    ),
    move: write('Move', (sources: string[], folder: string, options?: OperationOptions) =>
      transfer('move', sources, folder, options)
    ),
    copy: write('Copy', (sources: string[], folder: string, options?: OperationOptions) =>
      transfer('copy', sources, folder, options)
    ),
  }
}
